import { useReactToPrint } from "react-to-print";
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  RefObject,
} from "react";
import { useQueryClient, UseQueryResult, QueryKey } from "@tanstack/react-query";
import { EXPORT_TEMPLATES } from "../neighborhoodSearch/constants";
import pdfDownload from "../../components/common/pdfDownload";
import useScrollToManager from "src/hooks/useScrollToManager";
import {
  useListingUpdateTagsMutation,
  useListingAddNoteMutation,
  useListingDeleteNoteMutation,
  useToggleContactIsDisabledMutation,
} from "../../hooks/useListing";
import {
  Listing,
  EnumProductTypes,
  ContactInfo,
  Note,
  Tag,
} from "../../types/listing";
import { generateAveryLabelsPDF } from "../../utils/print";
import { exportDomToPdf } from "../../utils/pdfExport";
import { OutputType } from "./PrintExportDialog";
import { SORT_KEY_DELIMITER } from "../../constants/ui";

/**
 * Converts a Listing to LabelLead format for Avery labels
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertListingToLabelLead(listing: Listing): any {
  return {
    id: listing.id,
    propertyInfo: {
      ownerInfo: {
        partyOwners: [{
          fullName: listing.ownerContactInfo?.fullName || 'Name Not Available'
        }]
      },
      streetNumber: listing.address?.streetAddress?.split(' ')[0] || '',
      streetName: listing.address?.streetAddress?.split(' ').slice(1).join(' ') || '',
      streetSuffix: '', // Not available in Listing structure
      streetPostDirection: '', // Not available in Listing structure
      city: listing.address?.city || '',
      state: listing.address?.state || '',
      zipCode: listing.address?.postalCode || ''
    }
  };
}

export enum PrintType {
  single = "single",
  multiple = "multiple",
}

export enum SelectedListingsType {
  selections = "selections",
  all = "all",
}

export enum SearchResultsFormatType {
  list = "list",
  map = "map",
}

export type SidebarType = "details" | "filters" | null;

export const SIDEBAR_TYPES = {
  DETAILS: "details" as const,
  FILTERS: "filters" as const,
} as const;


interface GeoLocation {
  lat: number;
  lng: number;
}

interface SearchListingsData {
  total_records: number;
  listings: Listing[];
}

interface ListingInfo {
  listing: Listing;
  queryKey: QueryKey;
}

interface UseSearchListingsPageParams {
  searchQueryKey?: string;
  exportedCsvFileName?: string;
  productType: EnumProductTypes;
  suppressDetailsOpening?: boolean;
  totalRecords: number;
  listings: Listing[];
  csvApiUrl: string;
  allListingsQuery: UseQueryResult<SearchListingsData>;
  fetchListingsInChunks?: (
    onProgress: (completed: number, total: number) => void,
    cancelled: { current: boolean },
  ) => Promise<Listing[]>;
  sortField: string;
  sortDir: string;
  /** Optional key that changes when search criteria change (excluding pagination).
   *  When this changes, the selected listing will be reset to allow auto-selection of the first result. */
  searchCriteriaKey?: string;
}

interface UseSearchListingsPageResult {
  addScrollToItem: (
    element: HTMLElement,
    id: string | number,
    index: number
  ) => void;
  detailsListing: Listing | null;
  detailsListingId: string | null;
  downloadListings: () => Promise<void>;
  exportListingsToPdf: () => Promise<void>;
  isSidebarOpen: boolean;
  getDetailsListingInfoFromCache: ({
    detailsListingId,
  }: {
    detailsListingId: string | null;
  }) => ListingInfo | null;
  getDetailsListingTags: ({
    detailsListingId,
  }: {
    detailsListingId: string;
  }) => Tag[];
  handleDeleteNoteClick: ({
    listingId,
    noteId,
  }: {
    listingId: string;
    noteId: string;
  }) => Promise<void>;
  handleAddNoteClick: ({
    listingId,
    note,
  }: {
    listingId: string;
    note: string;
  }) => Promise<void>;
  handleTagsSaveClick: ({
    listingId,
    tags,
    previousTags,
  }: {
    listingId: string;
    tags: Tag[];
    previousTags?: Tag[];
  }) => Promise<void>;
  isDownloadingListings: boolean;
  isFetchingAllListings: boolean;
  isReadyToPrint: boolean;
  isSelectAllChecked: boolean;
  isBatchSelectionActive: boolean;
  selectedListings: Listing[];
  mapCenter: GeoLocation | undefined;
  manageNotesListing: Listing | null | undefined;
  numPrintRecords: number;
  optimisticUpdateListing: (updatedListing: Listing) => Promise<Listing[]>;
  printListings: () => Promise<void>;
  printAveryLabels: (exportProcess?: 'download' | 'print') => Promise<void>;
  printContentRef: RefObject<HTMLDivElement>;
  printType: PrintType;
  scrollToItemWithId: (
    id: string | number,
    behavior?: ScrollBehavior,
    block?: ScrollLogicalPosition
  ) => void;
  scrollToItemWithIndex: (
    index: number,
    behavior?: ScrollBehavior,
    block?: ScrollLogicalPosition
  ) => void;
  scrollToTop: () => void;
  selectedItems: Set<string>;
  selectedListingsType: SelectedListingsType;
  selectedPageListing: Listing | null;
  selectedSort: string;
  selectListing: (
    listing: Listing | null,
    suppressDetailsOpen?: boolean
  ) => void;
  setMapCenter: (center: GeoLocation | undefined) => void;
  setPrintType: (type: PrintType) => void;
  setSelectedListingsType: (type: SelectedListingsType) => void;
  setSelectedPageListing: (listing: Listing | null) => void;
  setSearchResultsFormat: (format: SearchResultsFormatType) => void;
  searchResultsFormat: SearchResultsFormatType;
  manageTagsListing: Listing | null | undefined;
  toggleSidebar: (isOpen?: boolean) => void;
  toggleItemIsSelected: (id: string) => void;
  setManageNotesListing: (listing: Listing | null | undefined) => void;
  toggleSelectAll: (newIsChecked: boolean) => void;
  toggleIsBatchSelectionActive: () => void;
  setManageTagsListing: (listing: Listing | null | undefined) => void;
  updateDetailsListingNotes: (updatedNotes: Note[]) => void;
  updateDetailsListingTags: (updatedTags: Tag[]) => void;
  handleToggleContactIsDisabled: (
    contact: ContactInfo,
    isDisabled?: boolean
  ) => Promise<void>;
  handlePrintSingleListingClick: (listing: Listing) => void;
  isPrintingSingleListing: boolean;
  setIsPrintingSingleListing: (value: boolean) => void;
  isPrintExportDialogOpen: boolean;
  setIsPrintExportDialogOpen: (value: boolean) => void;
  outputType: OutputType;
  setOutputType: (value: OutputType) => void;
  handlePrintExportDialogOpenChange: (open: boolean) => void;
  selectedExportTemplate: string;
  setSelectedExportTemplate: (template: string) => void;
  exportTemplateOptions: Array<{ value: string; label: string; }>;
  getListingsForSendCards: (
    onProgress?: (completed: number, total: number) => void,
    cancelled?: { current: boolean },
  ) => Promise<Listing[]>;
  getSendCardsCount: () => number;
}

const useSearchListingsPage = ({
  searchQueryKey,
  exportedCsvFileName,
  productType,
  suppressDetailsOpening = false,
  totalRecords,
  listings,
  csvApiUrl: baseCsvApiUrl,
  allListingsQuery,
  fetchListingsInChunks,
  sortField,
  sortDir,
  searchCriteriaKey,
}: UseSearchListingsPageParams): UseSearchListingsPageResult => {
  const queryClient = useQueryClient();

  // Printing
  const printContentRef = useRef<HTMLDivElement>(null);
  const [printType, setPrintType] = useState<PrintType>(PrintType.single);
  const [isBatchSelectionActive, setIsBatchSelectionActive] =
    useState<boolean>(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isReadyToPrint, setIsReadyToPrint] = useState<boolean>(false);
  const [isPrintingSingleListing, setIsPrintingSingleListing] = useState<boolean>(false);
  const [isPrintExportDialogOpen, setIsPrintExportDialogOpen] = useState<boolean>(false);
  const [outputType, setOutputType] = useState<OutputType>(OutputType.print);
  
  // Export template state management
  const [selectedExportTemplate, setSelectedExportTemplate] = useState<string>(EXPORT_TEMPLATES[0]?.id || "");
  
  const exportTemplateOptions = useMemo(
    () =>
      EXPORT_TEMPLATES.map((template) => ({
        value: template.id,
        label: template.name,
      })),
    []
  );

  // Enhance csvApiUrl to include the selected export template  
  const csvApiUrl = useMemo(() => {
    const templateParam = selectedExportTemplate === 'everything' ? 'default' : selectedExportTemplate;
    return templateParam 
      ? `${baseCsvApiUrl}${baseCsvApiUrl.includes('?') ? '&' : '?'}template=${encodeURIComponent(templateParam)}`
      : baseCsvApiUrl;
  }, [baseCsvApiUrl, selectedExportTemplate]);

  const printOne = useReactToPrint({
    contentRef: printContentRef,
  });

  const printAll = useReactToPrint({
    contentRef: printContentRef,
  });


  const { addScrollToItem, scrollToItemWithId, scrollToItemWithIndex } =
    useScrollToManager();
  const [isDownloadingListings, setIsDownloadingListings] =
    useState<boolean>(false);
  const [manageNotesListingId, setManageNotesListingId] = useState<string | null>(null);
  const [detailsListingId, setDetailsListingId] = useState<string | null>(null);
  const [selectedPageListing, setSelectedPageListing] =
    useState<Listing | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [manageTagsListing, setManageTagsListing] = useState<Listing | null | undefined>();
  const [mapCenter, setMapCenter] = useState<GeoLocation | undefined>();
  const [selectedListingsType, setSelectedListingsType] =
    useState<SelectedListingsType>(SelectedListingsType.all);
  const [searchResultsFormat, setSearchResultsFormat] =
    useState<SearchResultsFormatType>(SearchResultsFormatType.list);

  const { mutateAsync: addNote } = useListingAddNoteMutation({ productType });
  const { mutateAsync: deleteNote } = useListingDeleteNoteMutation({
    productType,
  });
  const { mutateAsync: saveTags } = useListingUpdateTagsMutation({
    productType,
  });
  const { mutateAsync: toggleContactIsDisabled } =
    useToggleContactIsDisabledMutation({ productType });


  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const {
    isLoading: isLoadingAllListings,
    isFetching: isFetchingAllListings,
    refetch: fetchAllListings,
    data: { listings: allFetchedListings } = {},
  } = allListingsQuery;

  const getDetailsListingInfoFromCache = useCallback(
    ({
      detailsListingId,
    }: {
      detailsListingId: string | null;
    }): ListingInfo | null => {
      if (!detailsListingId) {
        return null;
      }

      const searchResults = queryClient.getQueriesData({
        predicate: (query) => {
          return query.queryKey.includes(searchQueryKey);
        },
      });

      const foundQuery = searchResults?.find((searchResult) =>
        (searchResult?.[1] as SearchListingsData)?.listings?.find(
          (listing: Listing) => detailsListingId === listing.id
        )
      );

      if (!foundQuery) {
        return null;
      }

      const listings = (foundQuery?.[1] as SearchListingsData)?.listings;
      const foundListing = listings?.find(
        (listing: Listing) => detailsListingId === listing.id
      );

      if (!foundListing) {
        return null;
      }

      return {
        listing: foundListing,
        queryKey: foundQuery[0],
      };
    },
    [queryClient, searchQueryKey]
  );

  const detailsListingInfo = useMemo(() => {
    return detailsListingId
      ? getDetailsListingInfoFromCache({ detailsListingId })
      : null;
  }, [detailsListingId, getDetailsListingInfoFromCache]);

  const detailsListing = useMemo(
    () => detailsListingInfo?.listing || null,
    [detailsListingInfo]
  );

  const getDetailsListingTags = useCallback(
    ({ detailsListingId }: { detailsListingId: string }): Tag[] => {
      const cachedDetailsListing = getDetailsListingInfoFromCache({
        detailsListingId,
      })?.listing;
      return cachedDetailsListing?.tags || [];
    },
    [getDetailsListingInfoFromCache]
  );

  const optimisticUpdateListing = useCallback(
    async (updatedListing: Listing): Promise<Listing[]> => {
      if (!detailsListingInfo?.queryKey) {
        return [];
      }

      const { queryKey } = detailsListingInfo;

      await queryClient.cancelQueries({ queryKey });

      const searchResults = queryClient.getQueryData(
        queryKey
      ) as SearchListingsData;

      const updatedListings = searchResults?.listings?.reduce(
        (acc: Listing[], listing: Listing) => {
          if (listing.id === updatedListing.id) {
            return [...acc, updatedListing];
          }
          return [...acc, listing];
        },
        []
      );

      queryClient.setQueryData(queryKey, {
        ...searchResults,
        listings: updatedListings,
      });

      return updatedListings || [];
    },
    [detailsListingInfo, queryClient]
  );

  const optimisticUpdateDetailsListingTags = useCallback(
    (updatedTags: Tag[]) => {
      const cachedDetailsListing = getDetailsListingInfoFromCache({
        detailsListingId,
      })?.listing;
      if (cachedDetailsListing) {
        optimisticUpdateListing({
          ...cachedDetailsListing,
          tags: updatedTags,
        });
      }
    },
    [detailsListingId, optimisticUpdateListing, getDetailsListingInfoFromCache]
  );

  const optimisticUpdateDetailsListingNotes = useCallback(
    (updatedNotes: Note[]) => {
      const cachedDetailsListing = getDetailsListingInfoFromCache({
        detailsListingId,
      })?.listing;
      if (cachedDetailsListing) {
        optimisticUpdateListing({
          ...cachedDetailsListing,
          notes: updatedNotes,
        });
      }
    },
    [detailsListingId, optimisticUpdateListing, getDetailsListingInfoFromCache]
  );


  const selectedSort = useMemo((): string => {
    if (sortField && sortDir) {
      return `${sortField}${SORT_KEY_DELIMITER}${sortDir}`;
    }
    return "";
  }, [sortField, sortDir]);

  const numPrintRecords = useMemo(() => {
    if (selectedListingsType === SelectedListingsType.selections) {
      return selectedItems.size;
    }

    return totalRecords || 0;
  }, [selectedItems, totalRecords, selectedListingsType]);

// Track when search criteria changes to reset selection
const previousSearchCriteriaKey = useRef(searchCriteriaKey);
// Track which first listing ID we've auto-selected for this criteria
const autoSelectedFirstListingId = useRef<string | null>(null);

// When search criteria changes, clear selection and reset auto-select tracking
useEffect(() => {
  if (searchCriteriaKey !== undefined && previousSearchCriteriaKey.current !== searchCriteriaKey) {
    previousSearchCriteriaKey.current = searchCriteriaKey;
    // Clear selection and reset tracking so we auto-select the new first listing
    setDetailsListingId(null);
    setSelectedPageListing(null);
    setSelectedItems(new Set());
    setIsSelectAllChecked(false);
    autoSelectedFirstListingId.current = null;
  }
}, [searchCriteriaKey]);

// Auto-select first listing when:
// 1. Listings load and nothing is selected (initial load)
// 2. The first listing changed (new search results arrived)
useEffect(() => {
  if (listings && listings.length > 0) {
    const firstListing = listings[0];
    const firstListingChanged = firstListing.id !== autoSelectedFirstListingId.current;

    if (!selectedPageListing || firstListingChanged) {
      setSelectedPageListing(firstListing);
      if (firstListing.geolocation?.lat && firstListing.geolocation?.lng) {
        setMapCenter(firstListing.geolocation);
      } else {
        setMapCenter({ lat: 0, lng: 0 });
      }
      autoSelectedFirstListingId.current = firstListing.id;
    }
  }
}, [listings, selectedPageListing]);

// Center map whenever selectedPageListing changes
useEffect(() => {
  if (selectedPageListing) {
    if (selectedPageListing.geolocation?.lat && selectedPageListing.geolocation?.lng) {
      setMapCenter(selectedPageListing.geolocation);
    } else {
      // Listing has no geo data - set to 0,0 to trigger "no location data" overlay
      setMapCenter({ lat: 0, lng: 0 });
    }
  }
}, [selectedPageListing]);

  useEffect(() => {
    if (!isReadyToPrint || !printType) {
      return;
    }
    setTimeout(() => {
      printType === PrintType.single ? printOne() : printAll();
    }, 500);
    setIsReadyToPrint(false);
  }, [
    isReadyToPrint,
    printOne,
    printAll,
    printType,
    setPrintType,
    setIsReadyToPrint,
  ]);

  useEffect(() => {
    if (selectedItems.size > 0) {
      setSelectedListingsType(SelectedListingsType.selections);
    }
  }, [selectedItems.size, setSelectedListingsType]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if 'P' is pressed along with 'Control' or 'Command'
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "p") {
        event.preventDefault();
        printListings();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line
  }, []);

  const toggleSidebar = useCallback((isOpen?: boolean) => {
    const shouldOpen = isOpen ?? !isSidebarOpen;
    setIsSidebarOpen(shouldOpen);
  }, [isSidebarOpen]);


  const selectListing = useCallback(
    (listing: Listing | null, suppressDetailsOpen?: boolean) => {
      if (!suppressDetailsOpen && !suppressDetailsOpening) {
        setDetailsListingId(listing?.id || null);
        if (listing) {
          setIsSidebarOpen(true);
        }
      }
      listing && setSelectedPageListing(listing);
    },
    [setSelectedPageListing, suppressDetailsOpening]
  );

  const toggleSelectAll = useCallback(
    (newIsChecked: boolean) => {
      setIsSelectAllChecked(newIsChecked);
      if (newIsChecked) {
        // Add current page's items to existing selections
        const pageIds = listings?.map((listing) => listing.id) || [];
        setSelectedItems((prev) => new Set([...prev, ...pageIds]));
      } else {
        // Remove current page's items from selections
        const pageIds = new Set(listings?.map((listing) => listing.id) || []);
        setSelectedItems((prev) => {
          const updated = new Set(prev);
          pageIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }
    },
    [listings]
  );

  const toggleIsBatchSelectionActive = useCallback(() => {
    setIsBatchSelectionActive(!isBatchSelectionActive);
    setIsSelectAllChecked(false);
    setSelectedItems(new Set());
  }, [isBatchSelectionActive, setIsBatchSelectionActive, setSelectedItems]);

  const selectedListings = useMemo(() => {
    if (selectedListingsType === SelectedListingsType.selections) {
      // Collect from all cached pages, not just current page
      if (allFetchedListings && allFetchedListings.length > 0) {
        // Use all fetched listings if available
        return allFetchedListings.filter((listing) => {
          return selectedItems.has(listing.id);
        });
      }

      // Otherwise, collect from all cached search pages (with deduplication)
      const searchResults = queryClient.getQueriesData({
        predicate: (query) => {
          return query.queryKey.includes(searchQueryKey);
        },
      });

      const seenIds = new Set<string>();
      const allCachedListings: Listing[] = [];
      searchResults.forEach((searchResult) => {
        const listings = (searchResult?.[1] as SearchListingsData)?.listings;
        if (listings) {
          listings.forEach((listing) => {
            if (!seenIds.has(listing.id)) {
              seenIds.add(listing.id);
              allCachedListings.push(listing);
            }
          });
        }
      });

      return allCachedListings.filter((listing) => {
        return selectedItems.has(listing.id);
      });
    }
    return allFetchedListings || [];
  }, [allFetchedListings, selectedListingsType, selectedItems, queryClient, searchQueryKey]);

  const downloadListings = useCallback(async () => {
    if (isDownloadingListings) {
      return;
    }

    try {
      setIsDownloadingListings(true);

      let finalCsvUrl = csvApiUrl;

      // If we have selected items, add them as comma-delimited ids parameter
      if (selectedListingsType === SelectedListingsType.selections && selectedItems.size > 0) {
        const selectedIds = Array.from(selectedItems).join(',');
        const urlObj = new URL(finalCsvUrl, window.location.origin);
        urlObj.searchParams.set('ids', selectedIds);
        finalCsvUrl = urlObj.toString();
      }

      await pdfDownload({
        url: finalCsvUrl,
        fileName: exportedCsvFileName || "listings",
        extension: "csv",
        onComplete: () => {
          setIsDownloadingListings(false);
        },
      });
    } finally {
      setIsDownloadingListings(false);
    }
  }, [
    csvApiUrl,
    exportedCsvFileName,
    isDownloadingListings,
    selectedListingsType,
    selectedItems,
  ]);

  // Export listings to PDF method using DOM export
  const exportListingsToPdf = useCallback(async () => {
    if (!allListingsQuery || isDownloadingListings || allListingsQuery.isFetching) {
      return;
    }

    try {
      setIsDownloadingListings(true);
      
      // First, fetch the data to ensure print content is ready
      const result = await allListingsQuery.refetch();
      
      if (result.data?.listings && printContentRef.current) {
        // Verify that the print content is actually rendered
        const printElement = printContentRef.current;
        const hasContent = printElement.children.length > 0;
        
        if (!hasContent) {
          console.warn('Print content is not yet rendered. Listings for print may not be loaded.');
          // Force a small delay to allow React to render the content
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const filename = `listings-${new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit'
        }).replace(/\//g, '-')}.pdf`;

        try {
          // Try the DOM-to-PDF approach first for direct download
          console.log('Starting PDF export of listings...');
          await exportDomToPdf(
            printContentRef,
            filename,
            {
              timeout: 60000, // 60 seconds for large listings
              margin: 0.5,
              format: 'letter',
              orientation: 'portrait',
              quality: 0.6, // Reduced quality for faster generation
              scale: 0.8 // Reduced scale for faster rendering
            }
          );
          console.log('PDF export completed successfully!');
        } catch (domToPdfError) {
          console.error('PDF export failed:', domToPdfError);
          
          // Show user instruction for manual PDF export
          alert(
            'PDF export failed. Please use your browser\'s print function (Ctrl/Cmd+P) and select "Save as PDF" as the destination.'
          );
        }
      } else {
        // Fallback: Show user instruction
        alert('PDF export not available. Please use your browser\'s print function (Ctrl/Cmd+P) and select "Save as PDF" as the destination.');
      }
    } catch (error) {
      console.error('Error exporting listings to PDF:', error);
      alert('PDF export failed. Please try again.');
    } finally {
      setIsDownloadingListings(false);
    }
  }, [allListingsQuery, isDownloadingListings]);

  const printListings = useCallback(async () => {
    if (selectedListingsType === SelectedListingsType.selections) {
      if (!selectedItems.size) {
        return;
      }
      // Print uses selectedListings filtered from paged caches (from pages user navigated to)
      setIsReadyToPrint(true);
      return;
    }

    // Print all results - fetch all listings for map
    if (isLoadingAllListings) {
      return;
    }
    const resp = await fetchAllListings();
    if (resp.data && resp.data.total_records > 0) {
      setIsReadyToPrint(true);
    }
  }, [
    fetchAllListings,
    setIsReadyToPrint,
    selectedListingsType,
    selectedItems,
    isLoadingAllListings,
  ]);

  const printAveryLabels = useCallback(async (exportProcess: 'download' | 'print' = 'print') => {
    try {
      let listingsToProcess: Listing[] = [];

      if (selectedListingsType === SelectedListingsType.selections) {
        // If we already have allFetchedListings, use those; otherwise fetch
        if (allFetchedListings && allFetchedListings.length > 0) {
          listingsToProcess = allFetchedListings.filter((listing) => {
            return selectedItems.has(listing.id);
          });
        } else {
          const { data: { listings: fetchedListings } = {} } = await fetchAllListings();
          listingsToProcess = (fetchedListings || []).filter((listing) => {
            return selectedItems.has(listing.id);
          });
        }
        if (listingsToProcess.length === 0) {
          return;
        }
      } else {
        const { data: { listings: fetchedListings, total_records: totalRecords } = {} } = await fetchAllListings();
        if (totalRecords === 0) {
          return;
        }
        listingsToProcess = fetchedListings || [];
      }

      const labelLeads = listingsToProcess.filter(listing => !!listing?.id).map(convertListingToLabelLead);

      generateAveryLabelsPDF({
        title: 'Property Listings',
        leads: labelLeads,
        action: exportProcess as 'print' | 'download'
      });

    } catch (error) {
      console.error("Error generating Avery labels:", error);
    }
  }, [allFetchedListings, fetchAllListings, selectedListingsType, selectedItems]);

  // Get count of listings that would be sent (for limit checking before fetching)
  const getSendCardsCount = useCallback((): number => {
    if (isBatchSelectionActive && selectedItems.size > 0) {
      return selectedItems.size;
    }
    return totalRecords;
  }, [isBatchSelectionActive, selectedItems, totalRecords]);

  // Get listings for send cards - if items are selected use those, otherwise fetch all
  const getListingsForSendCards = useCallback(async (
    onProgress?: (completed: number, total: number) => void,
    cancelled?: { current: boolean },
  ): Promise<Listing[]> => {
    // If batch selection is active and items are selected, use those
    if (isBatchSelectionActive && selectedItems.size > 0) {
      // First check if we already have allFetchedListings
      if (allFetchedListings && allFetchedListings.length > 0) {
        return allFetchedListings.filter((listing) => selectedItems.has(listing.id));
      }

      // Try to collect from all cached pages first
      const searchResults = queryClient.getQueriesData({
        predicate: (query) => {
          return query.queryKey.includes(searchQueryKey);
        },
      });

      const cachedListings: Listing[] = [];
      searchResults.forEach((searchResult) => {
        const listings = (searchResult?.[1] as SearchListingsData)?.listings;
        if (listings) {
          cachedListings.push(...listings);
        }
      });

      const selectedFromCache = cachedListings.filter((listing) => selectedItems.has(listing.id));
      if (selectedFromCache.length > 0) {
        return selectedFromCache;
      }

      // Only fetch if items aren't in cache
      const { data } = await fetchAllListings();
      return (data?.listings || []).filter((listing) => selectedItems.has(listing.id));
    }

    // Use chunked fetch with progress if available
    if (fetchListingsInChunks && onProgress && cancelled) {
      return fetchListingsInChunks(onProgress, cancelled);
    }

    // Fallback to single fetch
    const { data } = await fetchAllListings();
    return data?.listings || [];
  }, [isBatchSelectionActive, selectedItems, allFetchedListings, fetchAllListings, fetchListingsInChunks, queryClient, searchQueryKey]);

  const toggleItemIsSelected = useCallback(
    (id: string) => {
      const newSelectedItems = new Set(selectedItems);
      if (newSelectedItems.has(id)) {
        newSelectedItems.delete(id);
      } else {
        newSelectedItems.add(id);
      }
      setSelectedItems(newSelectedItems);
    },
    [selectedItems, setSelectedItems]
  );

  const handleTagsSaveClick = useCallback(
    async ({
      listingId,
      tags,
      previousTags,
    }: {
      listingId: string;
      tags: Tag[];
      previousTags?: Tag[];
    }) => {
      try {
        await saveTags({ listingId, tags, previousTags });
        optimisticUpdateDetailsListingTags(tags);
        setManageTagsListing(null);
      } catch (error) {
        console.error("Error saving tags:", error);
      }
    },
    [optimisticUpdateDetailsListingTags, saveTags, setManageTagsListing]
  );

  const handleAddNoteClick = useCallback(
    async ({ listingId, note }: { listingId: string; note: string }) => {
      if (!note || note.length === 0 || !listingId) {
        return;
      }
      try {
        // The mutation handles cache updates via onMutate (optimistic) and onSuccess (server response)
        await addNote({
          listingId,
          note,
        });
      } catch (error) {
        console.error("Error adding note:", error);
      }
    },
    [addNote]
  );

  const handleToggleContactIsDisabled = useCallback(
    async (contact: ContactInfo, isDisabled?: boolean) => {
      try {
        await toggleContactIsDisabled({
          phone: contact.phone || '',
          listingId: contact?.listingId || '',
          propertyId: (contact as ContactInfo & { propertyId?: string })?.propertyId || '',
          isDisabled: isDisabled === undefined ? !contact.hidden : isDisabled,
        });
      } catch (error) {
        console.error("Error toggling contact is disabled:", error);
      }
    },
    [toggleContactIsDisabled]
  );

  const handleDeleteNoteClick = useCallback(
    async ({ listingId,  noteId }: { listingId: string; noteId: string }) => {
      try {
        await deleteNote({
          listingId,
          noteId,
        });
        const listing = getDetailsListingInfoFromCache({
          detailsListingId: listingId,
        })?.listing;
        const updatedNotesList = (listing?.notes || []).filter(
          (item) => item.id !== noteId
        );
        optimisticUpdateDetailsListingNotes(updatedNotesList);
      } catch (error) {
        console.error("Error deleting note:", error);
      }
    },
    [
      optimisticUpdateDetailsListingNotes,
      deleteNote,
      getDetailsListingInfoFromCache,
    ]
  );

  const handlePrintSingleListingClick = useCallback((listing: Listing) => {
    // First clear all selections
    toggleSelectAll(false);
    // Then select only this listing
    toggleItemIsSelected(listing.id);
    // Set to use selections mode
    setSelectedListingsType(SelectedListingsType.selections);
    setIsPrintingSingleListing(true);
    // Open print dialog with print output type
    setOutputType(OutputType.print);
    setIsPrintExportDialogOpen(true);
  }, [toggleSelectAll, toggleItemIsSelected, setSelectedListingsType]);

  const handlePrintExportDialogOpenChange = useCallback((open: boolean) => {
    setIsPrintExportDialogOpen(open);
    if (!open && isPrintingSingleListing) {
      // Reset all state we set in handlePrintSingleListingClick
      setIsPrintingSingleListing(false);
      toggleSelectAll(false); // Clear the selection
      setSelectedListingsType(SelectedListingsType.all); // Reset to default
    }
  }, [isPrintingSingleListing, setIsPrintingSingleListing, toggleSelectAll, setSelectedListingsType]);

  // Derive manageNotesListing from the listings array using the ID
  // We use listings as a dependency because it's reactive to cache changes
  const manageNotesListing = useMemo(() => {
    if (!manageNotesListingId) return null;
    return listings?.find(listing => listing.id === manageNotesListingId) || null;
  }, [manageNotesListingId, listings]);

  // Wrapper to convert listing to ID for setting notes dialog
  const setManageNotesListing = useCallback((listing: Listing | null | undefined) => {
    setManageNotesListingId(listing?.id || null);
  }, []);

  return {
    addScrollToItem,
    detailsListing,
    detailsListingId,
    downloadListings,
    exportListingsToPdf,
    isSidebarOpen,
    getDetailsListingInfoFromCache,
    getDetailsListingTags,
    handleDeleteNoteClick,
    handleAddNoteClick,
    handleTagsSaveClick,
    isDownloadingListings,
    isReadyToPrint,
    isSelectAllChecked,
    isBatchSelectionActive,
    selectedListings,
    mapCenter,
    manageNotesListing,
    setManageNotesListing,
    numPrintRecords,
    optimisticUpdateListing,
    printListings,
    printAveryLabels,
    isFetchingAllListings,
    printContentRef,
    printType,
    scrollToItemWithId,
    scrollToItemWithIndex,
    scrollToTop,
    selectedItems,
    selectedListingsType,
    selectedPageListing,
    selectedSort,
    selectListing,
    setMapCenter,
    setPrintType,
    setSelectedListingsType,
    setSelectedPageListing,
    setSearchResultsFormat,
    searchResultsFormat,
    manageTagsListing,
    toggleSidebar,
    toggleItemIsSelected,
    toggleSelectAll,
    toggleIsBatchSelectionActive,
    setManageTagsListing,
    updateDetailsListingNotes: optimisticUpdateDetailsListingNotes,
    updateDetailsListingTags: optimisticUpdateDetailsListingTags,
    handleToggleContactIsDisabled,
    handlePrintSingleListingClick,
    isPrintingSingleListing,
    setIsPrintingSingleListing,
    isPrintExportDialogOpen,
    setIsPrintExportDialogOpen,
    outputType,
    setOutputType,
    handlePrintExportDialogOpenChange,
    selectedExportTemplate,
    setSelectedExportTemplate,
    exportTemplateOptions,
    getListingsForSendCards,
    getSendCardsCount,
    productType,
  };
};

export default useSearchListingsPage;
